import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { AuthUser } from "../../worker/shared/authContracts";
import { authApi, isCloudPreview, documentStore } from "../aee/api";

const Context = createContext<{
  loading: boolean;
  user: AuthUser | null;
  error: string;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}>({
  loading: true,
  user: null,
  error: "",
  refresh: async () => {},
  logout: async () => {},
});
// eslint-disable-next-line react-refresh/only-export-components
export const useAccount = () => useContext(Context);
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    loading: boolean;
    user: AuthUser | null;
    error: string;
  }>({ loading: true, user: null, error: "" });
  const generation = useRef(0), sessionOwner = useRef<string | null>(null);
  const refresh = useCallback(async () => {
    const attempt = ++generation.current;
    if (isCloudPreview()) {
      setState({ loading: false, user: null, error: "" });
      return;
    }
    try {
      const session = await authApi.session();
      if (attempt === generation.current) {
        const owner = session.authenticated && session.mode !== "development" ? session.user?.id || null : null;
        if (owner !== sessionOwner.current) { documentStore.clearPrefetch?.(); sessionOwner.current = owner; }
        if (session.authenticated && session.mode === "development") {
          setState({
            loading: false,
            user: null,
            error:
              "Sign in with Google or GitHub to open your account. Restart the development server if the test account is still enabled.",
          });
        } else setState({ loading: false, user: session.user, error: "" });
      }
    } catch {
      documentStore.clearPrefetch?.();
      if (attempt === generation.current)
        setState({
          loading: false,
          user: null,
          error:
            "Unable to check your session. Check your connection and try again.",
        });
    }
  }, []);
  useEffect(() => {
    void Promise.resolve().then(refresh);
    const focus = () => {
      void refresh();
    };
    window.addEventListener("focus", focus);
    window.addEventListener("aee-session-expired", focus);
    // Invalidate any unresolved bootstrap request on unmount.
    return () => {
      // This is an async request generation counter, not a DOM reference.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generation.current++;
      window.removeEventListener("focus", focus);
      window.removeEventListener("aee-session-expired", focus);
    };
  }, [refresh]);
  const logout = async () => {
    await authApi.logout();
    documentStore.clearPrefetch?.(); sessionOwner.current = null;
    generation.current++;
    setState({ loading: false, user: null, error: "" });
  };
  return (
    <Context.Provider value={{ ...state, refresh, logout }}>
      {children}
    </Context.Provider>
  );
}
export function AccountGate() {
  const account = useAccount(),
    location = useLocation();
  if (account.loading)
    return (
      <main className="account-screen">
        <p role="status">Checking your session…</p>
      </main>
    );
  if (account.error)
    return (
      <main className="account-screen">
        <p role="alert">{account.error}</p>
        <button onClick={() => void account.refresh()}>Try again</button>
      </main>
    );
  if (!account.user)
    return (
      <Navigate to="/signin" state={{ from: location.pathname }} replace />
    );
  if (
    (account.user.profileComplete === false || account.user.onboardingRequired || account.user.termsRequired) &&
    location.pathname !== "/complete-profile"
  )
    return <Navigate to="/complete-profile" replace />;
  return (
    <div key={account.user.id} style={{ display: "contents" }}>
      <Outlet />
    </div>
  );
}
